import {
  WSActions,
  WSControlGroup,
  WSDivider,
  WSForm,
  WSFormProps,
  WSList,
  WSSectionToolbar,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { COUNTRY_OPTIONS } from "../../../../constants/user";
import { getEntityTypeLabel } from "../../helpers/entityType";
import { FormData } from "../../types";
import { AccountHolderFields } from "./AccountHolderFields";
import { BusinessFields } from "./BusinessFields";
import { defaultValues as emptyDefaultValues } from "./defaultValues";
import { getValidationSchema } from "./validationSchema";

type Props = {
  defaultValues?: WSFormProps<FormData>["defaultValues"];
  onSubmit?: WSFormProps<FormData>["onSubmit"];
  onCancel?: () => void;
  ssnProvided?: boolean;
};

export const Form: React.FC<Props> = ({
  onSubmit,
  onCancel,
  defaultValues,
  ssnProvided
}) => {
  return (
    <WSForm<FormData>
      defaultValues={{
        ...emptyDefaultValues,
        ...defaultValues
      }}
      validationSchema={getValidationSchema({ ssnProvided })}
      onSubmit={onSubmit}
    >
      {formContext => (
        <>
          <WSSectionToolbar title="Account type" mb="L" />
          <WSList gap="2XL">
            <WSForm.Field
              name="country"
              onChange={(value: string | null) => {
                formContext.setValue("business.address.country", value);
                formContext.setValue("accountHolder.country", value);
              }}
              component={WSSelect}
              label="Country of operation"
              componentProps={{
                required: true,
                internalSearch: true,
                options: COUNTRY_OPTIONS
              }}
            />
            <WSForm.Value name="country">
              {country => (
                <WSForm.Field
                  name="type"
                  component={WSControlGroup}
                  componentProps={{
                    required: true,
                    type: "radio",
                    chip: true,
                    options: [
                      {
                        label: getEntityTypeLabel("Business", country),
                        value: "Business",
                        tooltip:
                          "Your entity information will be used on invoices and tax documentation"
                      },
                      {
                        label: getEntityTypeLabel("Individual", country),
                        value: "Individual",
                        tooltip:
                          "Your personal information will be used on invoices and tax documentation"
                      }
                    ]
                  }}
                />
              )}
            </WSForm.Value>

            <WSForm.Value name="type">
              {type => (
                <>
                  <WSForm.Field
                    hidden={type !== "Business"}
                    name="numberOfEmployees"
                    component={WSControlGroup}
                    label="How many employees do you have?"
                    componentProps={{
                      required: true,
                      type: "radio",
                      options: [
                        {
                          label: "None",
                          value: "None"
                        },
                        {
                          label: "1-10",
                          value: "1To10"
                        },
                        {
                          label: "11-50",
                          value: "11To50"
                        },
                        {
                          label: "51-250",
                          value: "51To250"
                        },
                        {
                          label: "Over 250",
                          value: "Over250"
                        }
                      ]
                    }}
                  />
                  <BusinessFields hidden={!type || type !== "Business"} />
                  <AccountHolderFields ssnProvided={ssnProvided} />
                </>
              )}
            </WSForm.Value>
          </WSList>

          <WSDivider my="2XL" />

          <WSActions
            orientation="horizontal"
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "Cancel",
                onClick: onCancel,
                visible: !!onCancel
              },
              {
                label: "Continue",
                type: "submit"
              }
            ]}
          />
          <WSText.ParagraphXs mt="XL" color="gray400">
            The information on this page is required to setup your Wingspan
            account, if you exit or leave without completing this step, you will
            return to this page the next time you sign-in
          </WSText.ParagraphXs>
        </>
      )}
    </WSForm>
  );
};
